// Etiquetas de apertura, elaboración y congelación (RD 1021/2022). Se usa con
// el móvil en la cocina: Etiquetar (imprimir), Abiertos (lo que sigue abierto
// y su caducidad) y Productos (vida útil y conservación de cada producto).
// La pestaña activa va en `?tab=`. Lo puede usar todo el personal.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListChecks, Printer, SlidersHorizontal } from 'lucide-react';
import { etiquetasAPI, textoErrorApi } from '../api/api';
import { useVistaEmpleado } from '../context/VistaEmpleadoContext';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Toaster } from '../components/ui/toaster';
import EtiquetarTab from '../components/etiquetas/EtiquetarTab';
import AbiertosTab from '../components/etiquetas/AbiertosTab';
import ProductosTab from '../components/etiquetas/ProductosTab';

const PESTANAS = [
  { clave: 'etiquetar', texto: 'Etiquetar', Icono: Printer },
  { clave: 'abiertos', texto: 'Abiertos', Icono: ListChecks },
  { clave: 'productos', texto: 'Productos', Icono: SlidersHorizontal },
];

const DATOS_VACIOS = { productos: [], grupos_ingrediente: [], grupos_plato: [], puede_editar: false };

const listaAperturas = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.aperturas)) return data.aperturas;
  return [];
};

const Etiquetas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { simulando } = useVistaEmpleado();
  const pedida = searchParams.get('tab');
  const pestana = PESTANAS.some((p) => p.clave === pedida) ? pedida : 'etiquetar';

  const [datos, setDatos] = useState(DATOS_VACIOS);
  const [cargandoProductos, setCargandoProductos] = useState(true);
  const [errorProductos, setErrorProductos] = useState('');
  const [aperturas, setAperturas] = useState([]);
  const [cargandoAperturas, setCargandoAperturas] = useState(true);
  const [errorAperturas, setErrorAperturas] = useState('');

  const cargarProductos = useCallback(async () => {
    setCargandoProductos(true);
    try {
      const { data } = await etiquetasAPI.productos();
      setDatos({
        productos: Array.isArray(data?.productos) ? data.productos : [],
        grupos_ingrediente: Array.isArray(data?.grupos_ingrediente) ? data.grupos_ingrediente : [],
        grupos_plato: Array.isArray(data?.grupos_plato) ? data.grupos_plato : [],
        puede_editar: !!data?.puede_editar,
      });
      setErrorProductos('');
    } catch (e) {
      setErrorProductos(textoErrorApi(e?.response?.data?.detail, 'No se pudieron cargar los productos.'));
    } finally {
      setCargandoProductos(false);
    }
  }, []);

  const cargarAperturas = useCallback(async () => {
    setCargandoAperturas(true);
    try {
      const { data } = await etiquetasAPI.aperturas('abierta');
      setAperturas(listaAperturas(data));
      setErrorAperturas('');
    } catch (e) {
      setErrorAperturas(textoErrorApi(e?.response?.data?.detail, 'No se pudo cargar lo abierto.'));
    } finally {
      setCargandoAperturas(false);
    }
  }, []);

  const recargar = useCallback(
    () => Promise.all([cargarProductos(), cargarAperturas()]),
    [cargarProductos, cargarAperturas],
  );

  useEffect(() => {
    recargar();
  }, [recargar]);

  // Tras guardar un producto en Productos (el PUT devuelve el producto efectivo)
  const actualizarProducto = useCallback((producto) => {
    if (!producto) return;
    setDatos((prev) => ({
      ...prev,
      productos: prev.productos.map((p) => (
        p.tipo === producto.tipo && p.id === producto.id ? { ...p, ...producto } : p
      )),
    }));
  }, []);

  const cambiarPestana = useCallback((valor) => {
    const siguiente = new URLSearchParams(searchParams);
    if (valor === 'etiquetar') siguiente.delete('tab');
    else siguiente.set('tab', valor);
    setSearchParams(siguiente, { replace: true });
  }, [searchParams, setSearchParams]);

  // Número de la pestaña Abiertos: lo que no está bien (caducado o a punto)
  const pendientes = useMemo(
    () => aperturas.filter((a) => a.estado_caducidad && a.estado_caducidad !== 'ok').length,
    [aperturas],
  );

  return (
    <div className="max-w-5xl mx-auto" data-testid="pagina-etiquetas">
      <Tabs value={pestana} onValueChange={cambiarPestana}>
        <div className="sticky top-[65px] md:top-0 z-20 -mx-4 md:-mx-6 px-4 md:px-6 py-2 bg-gray-50/95 backdrop-blur border-b border-gray-200">
          <div className="flex items-center justify-between gap-3 mb-2">
            <h1 className="text-xl font-bold text-gray-900">Etiquetas</h1>
            {aperturas.length > 0 && (
              <span className="text-xs text-gray-500">
                {aperturas.length === 1 ? '1 abierta' : `${aperturas.length} abiertas`}
              </span>
            )}
          </div>
          <TabsList className="h-auto w-full grid grid-cols-3 bg-white border border-gray-200 p-1 gap-1 rounded-xl">
            {PESTANAS.map(({ clave, texto, Icono }) => (
              <TabsTrigger
                key={clave}
                value={clave}
                className="h-11 flex items-center justify-center gap-1.5 px-2 rounded-lg text-sm font-semibold text-gray-600 data-[state=active]:bg-duetto-primary data-[state=active]:text-white data-[state=active]:shadow-none"
                data-testid={`etiquetas-tab-${clave}`}
              >
                <Icono className="w-4 h-4 hidden sm:block" />
                {texto}
                {clave === 'abiertos' && pendientes > 0 && (
                  <span className="min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[11px] font-bold leading-none">
                    {pendientes}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="etiquetar" className="mt-4">
          <EtiquetarTab
            productos={datos.productos}
            gruposIngrediente={datos.grupos_ingrediente}
            gruposPlato={datos.grupos_plato}
            aperturas={aperturas}
            cargando={cargandoProductos}
            error={errorProductos}
            onReintentar={cargarProductos}
            onRecargar={recargar}
            soloLectura={simulando}
          />
        </TabsContent>

        <TabsContent value="abiertos" className="mt-4">
          <AbiertosTab
            aperturas={aperturas}
            cargando={cargandoAperturas}
            error={errorAperturas}
            onReintentar={cargarAperturas}
            onRecargar={recargar}
            soloLectura={simulando}
          />
        </TabsContent>

        <TabsContent value="productos" className="mt-4">
          <ProductosTab
            productos={datos.productos}
            gruposIngrediente={datos.grupos_ingrediente}
            gruposPlato={datos.grupos_plato}
            puedeEditar={datos.puede_editar}
            cargando={cargandoProductos}
            error={errorProductos}
            onReintentar={cargarProductos}
            onProductoActualizado={actualizarProducto}
            onRecargar={cargarProductos}
            soloLectura={simulando}
          />
        </TabsContent>
      </Tabs>
      <Toaster />
    </div>
  );
};

export default Etiquetas;
